import React, { useEffect } from 'react';
import { Text } from '@charlietango/ui';
import { rem } from '../../styles/rem';

import { usePartyDetailsUrl } from '../../application/PartyDetailsUrlContext';
import { useDictionary } from '../../hooks/useDictionary';
import { colors } from '../../styles/colors';
import { readableListTitleTextWrap } from '../../styles/textWrap';
import { FontFamily, TextVariant } from '../../styles/typography';
import { thousandsSeparators } from '../../utils/format-utils';
import Anchor from '../Anchor/Anchor';
import Mark from '../Mark/Mark';
import PartyElectionBadges from './PartyElectionBadges';
import PartyListItem from './PartyListItem';
import {
  getElectionBadges,
  getPartyExpiryDateAriaLabel,
  getPartyHref,
  getPartyVoteAriaLabel,
  getVisibleExpiryDates,
} from './partyListDisplay';
import { partyTableSortOptions } from './partyListModel';
import type { PartyListParty, PartyTableSortOption } from './partyListModel';

type PartyListProps = {
  items?: PartyListParty[] | null;
  partyDetailsUrl: string;
  highlight?: string;
  renderTableHeader?: (option: PartyTableSortOption) => React.ReactNode;
  id?: string;
  className?: string;
  'aria-labelledby'?: string;
  'aria-live'?: 'off' | 'polite' | 'assertive';
};

const tableCellStyle: Record<string, any> = {
  height: rem(52),
  py: rem(12),
  px: rem(24),
  fontSize: rem(16),
  lineHeight: rem(20),
  fontWeight: 'inherit',
  textAlign: 'left',
  verticalAlign: 'middle',
  color: colors.base.Text.PrimaryStatic,
  backgroundColor: colors.components.partyListItem.Background.Default,
  '&:first-child': {
    borderTopLeftRadius: rem(8),
    borderBottomLeftRadius: rem(8),
  },
  '&:last-child': {
    borderTopRightRadius: rem(8),
    borderBottomRightRadius: rem(8),
  },
};

const tableRowStyle: Record<string, any> = {
  cursor: 'pointer',
  '&:hover > th, &:hover > td, &:focus-within > th, &:focus-within > td': {
    backgroundColor: colors.components.partyListItem.Background.Hover,
  },
  '&:hover [data-election-type-badge], &:focus-within [data-election-type-badge]': {
    backgroundColor: colors.components.fixedTop.Background.Default,
  },
};

const partyNameCellStyle: Record<string, any> = {
  ...tableCellStyle,
  p: 0,
  fontFamily: FontFamily.Heading,
  fontSize: rem(20),
  fontWeight: 500,
  lineHeight: rem(28),
};

const electionTypeCellStyle: Record<string, any> = {
  ...tableCellStyle,
  pl: rem(12),
  pr: rem(24),
  textAlign: 'right',
};

const tableHeaderCellStyle: Record<string, any> = {
  pb: rem(16),
  px: 0,
  textAlign: 'left',
  verticalAlign: 'bottom',
};

const tableHeaderContentStyle = {
  display: 'flex',
  px: rem(24),
};

function LinkedTableRow({
  children,
  linkRef,
  ...props
}: React.PropsWithoutRef<JSX.IntrinsicElements['tr']> & {
  linkRef: React.RefObject<HTMLAnchorElement>;
}) {
  return (
    <tr
      {...props}
      onClick={(event) => {
        props.onClick?.(event);
        if (event.defaultPrevented) return;

        const clickedInteractiveElement =
          event.target instanceof Element
            ? event.target.closest(
                'a, button, input, select, textarea, summary, [role="button"], [role="link"]',
              )
            : null;

        if (clickedInteractiveElement) {
          return;
        }

        linkRef.current?.click();
      }}
    >
      {children}
    </tr>
  );
}

type PartyTableRowProps = {
  party: PartyListParty;
  partyDetailsUrl: string;
  highlight?: string;
};

function PartyTableRow({
  party,
  partyDetailsUrl,
  highlight,
}: PartyTableRowProps) {
  const { t } = useDictionary();
  const linkRef = React.useRef<HTMLAnchorElement>(null);
  const visibleExpiryDates = getVisibleExpiryDates(party);
  const badges = getElectionBadges(party.type, t);
  const partyHref = getPartyHref(party, partyDetailsUrl);
  const voteAriaLabel = getPartyVoteAriaLabel(party, t);
  const expiryDateAriaLabel = getPartyExpiryDateAriaLabel(party, t);
  const electionTypeAriaLabel = `${t(
    'parties.partiesAlsoEuroparliment',
  )}: ${badges.map((badge) => badge.title).join(', ')}`;

  return (
    <LinkedTableRow sx={tableRowStyle} linkRef={linkRef}>
      <th
        scope="row"
        sx={{
          ...partyNameCellStyle,
        }}
      >
        <Anchor
          ref={linkRef}
          href={partyHref}
          sx={{
            display: 'block',
            px: rem(24),
            py: rem(12),
            color: 'inherit',
            textDecoration: 'none',
            whiteSpace: 'normal',
            ...readableListTitleTextWrap,
            '&:focus-visible': {
              outlineOffset: rem(-4),
            },
          }}
        >
          {highlight && party.name ? (
            <Mark highlight={highlight}>{party.name}</Mark>
          ) : (
            party.name
          )}
        </Anchor>
      </th>
      <td aria-label={voteAriaLabel} sx={tableCellStyle}>
        {thousandsSeparators(party.voteCount ?? 0)}
        {party.voteCount2 !== undefined
          ? ` / ${thousandsSeparators(party.voteCount2)}`
          : ''}
      </td>
      <td
        aria-label={expiryDateAriaLabel}
        sx={{ ...tableCellStyle, whiteSpace: 'nowrap' }}
      >
        {visibleExpiryDates.join(' / ')}
      </td>
      <td aria-label={electionTypeAriaLabel} sx={electionTypeCellStyle}>
        <PartyElectionBadges badges={badges} align="right" />
      </td>
    </LinkedTableRow>
  );
}

function PartyList({
  items,
  partyDetailsUrl,
  highlight,
  renderTableHeader,
  id,
  className,
  'aria-labelledby': ariaLabelledBy,
  'aria-live': ariaLive,
}: PartyListProps) {
  const { t } = useDictionary();
  const { setPartyDetailsUrl } = usePartyDetailsUrl();

  useEffect(() => {
    setPartyDetailsUrl(partyDetailsUrl);
  }, [partyDetailsUrl, setPartyDetailsUrl]);

  return (
    <>
      <table
        id={id}
        sx={{
          display: ['none', 'none', 'table'],
          width: `calc(100% - ${rem(80)})`,
          mx: rem(40),
          borderCollapse: 'collapse',
          borderSpacing: 0,
          tableLayout: 'fixed',
        }}
        className={className}
        aria-labelledby={ariaLabelledBy}
        aria-live={ariaLive}
      >
        <colgroup>
          <col sx={{ width: '40%' }} />
          <col sx={{ width: '24%' }} />
          <col />
          <col sx={{ width: rem(132) }} />
        </colgroup>
        <caption
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {t('parties.theParties', { count: items ? items.length : 0 })}
        </caption>
        <thead>
          <tr>
            <th scope="col" sx={tableHeaderCellStyle}>
              <div sx={tableHeaderContentStyle}>
                {renderTableHeader?.(partyTableSortOptions[0])}
              </div>
            </th>
            <th scope="col" sx={tableHeaderCellStyle}>
              <div sx={{ ...tableHeaderContentStyle, pr: 0 }}>
                {renderTableHeader?.(partyTableSortOptions[1])}
              </div>
            </th>
            <th scope="col" sx={tableHeaderCellStyle}>
              <div sx={tableHeaderContentStyle}>
                {renderTableHeader?.(partyTableSortOptions[2])}
              </div>
            </th>
            <th
              scope="col"
              sx={{
                ...tableHeaderCellStyle,
                textAlign: 'right',
              }}
            >
              <div
                sx={{
                  ...tableHeaderContentStyle,
                  justifyContent: 'flex-end',
                  pl: rem(12),
                  pr: rem(24),
                }}
              >
                {renderTableHeader?.(partyTableSortOptions[3])}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {!items || !items.length ? (
            <tr>
              <td colSpan={4} sx={{ textAlign: 'center', p: '1rem' }}>
                <Text variant={TextVariant.BodyMd}>
                  {t('parties.noResults')}
                </Text>
              </td>
            </tr>
          ) : null}
          {items?.map((party, index) => {
            return (
              <React.Fragment key={`${party.name}-${party.id}-${party.type}`}>
                {index > 0 ? (
                  <tr aria-hidden>
                    <td colSpan={4} sx={{ height: rem(4), p: 0 }} />
                  </tr>
                ) : null}
                <PartyTableRow
                  party={party}
                  partyDetailsUrl={partyDetailsUrl}
                  highlight={highlight}
                />
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <ul
        id={id ? `${id}-cards` : undefined}
        sx={{
          display: ['block', 'grid', 'none'],
          gridTemplateColumns: ['1fr', 'repeat(2, minmax(0, 1fr))', 'none'],
          gap: rem(8),
          p: 0,
          mx: 0,
          my: 0,
        }}
        className={className}
        aria-labelledby={ariaLabelledBy}
        aria-live={ariaLive}
      >
        {!items || !items.length ? (
          <li
            sx={{
              gridColumn: ['auto', '1 / -1'],
              textAlign: 'center',
              listStyle: 'none',
              p: '1rem',
            }}
            key="no-items"
          >
            <Text variant={TextVariant.BodyMd}>{t('parties.noResults')}</Text>
          </li>
        ) : null}
        {items?.map((party, index) => (
          <PartyListItem
            isFirstItem={index === 0}
            key={`${party.name}-${party.id}-${party.type}`}
            party={party}
            partyDetailsUrl={partyDetailsUrl}
            highlight={highlight}
          />
        ))}
      </ul>
    </>
  );
}

export default PartyList;
